import { A, CONTACT_EMAIL, H2, H3, LegalLayout, MIN_AGE, OPERATOR, P, REGISTERED_ADDRESS, SITE_URL, Ul } from './shared';

// Public, unauthenticated route (see App.tsx) - same content as
// landing/templates.mjs's renderPrivacy(), ported to this app's components.
export function PrivacyScreen() {
  return (
    <LegalLayout title="Privacy Policy">
      <P>
        This Privacy Policy explains what information zmist (operated by {OPERATOR}) collects when you use{' '}
        {SITE_URL} and the zmist web/iOS apps (the "Service"), why, and what choices you have. It's meant to be read
        alongside our <A to="/terms">Terms of Service</A> and <A to="/cookies">Cookie Policy</A>.
      </P>

      <H2>1. What we collect</H2>
      <H3>Account information</H3>
      <P>
        When you register, we store your email address, a display nickname, and a securely hashed password (we
        never store your password itself). If you sign in through Google, Microsoft, Apple, or LinkedIn instead, we
        receive your name and email from that provider - never your password with that provider, and never anything
        beyond name/email.
      </P>
      <H3>Channels, messages, and schemas</H3>
      <P>
        Everything you send into a channel - free-text messages, schema-triggered signals, and their timestamps
        and sender - is stored so the channel has real history, not just a live feed. The same is true of schemas
        and reports you create.
      </P>
      <H3>Uploaded images</H3>
      <P>Avatar and channel images you upload are stored in object storage (S3-compatible) associated with your account or channel.</P>
      <H3>Technical data</H3>
      <P>
        Like most web services, our infrastructure (including Cloudflare, which sits in front of our web traffic)
        logs standard connection information (IP address, request timing) for security and reliability - we don't
        use this to build an advertising profile of you.
      </P>

      <H2>2. Why we collect it</H2>
      <Ul>
        <li>
          <strong>To provide the Service</strong> - an account, channel history, and real-time messaging can't work
          without storing this.
        </li>
        <li>
          <strong>To send you transactional email</strong> - a password-reset link if you request one, or a
          channel-invite email if someone invites you. We don't send marketing email today.
        </li>
        <li>
          <strong>For security</strong> - detecting and responding to abuse or unauthorized access.
        </li>
      </Ul>

      <H2>3. What we don't do</H2>
      <P>
        We don't sell your personal information. We don't use third-party advertising trackers - see our{' '}
        <A to="/cookies">Cookie Policy</A> for the full, current list of what's actually set (today: essential local
        storage only).
      </P>

      <H2>4. Who we share it with</H2>
      <P>
        Only the infrastructure sub-processors that make the Service work - hosting, network/DNS, object storage,
        and transactional email. The current list, including what each one processes and where, is on our{' '}
        <A to="/subprocessors">Sub-processors</A> page, updated before we add a new one. None of them are permitted
        to use your data for their own purposes. Content you post in a channel is visible to that channel's other
        members by design - and, if you generate one, to anyone with that channel's public share link (see our{' '}
        <A to="/terms">Terms</A>, Section 6).
      </P>
      <P>We may disclose information if required by law, or to protect the rights, safety, or property of zmist or its users.</P>

      <H2>5. How long we keep it</H2>
      <P>
        We keep your account and channel data for as long as your account exists, so channel history stays intact.
        A password-reset link expires after 1 hour whether or not it's used. When you delete your account (see
        Section 7) it is deactivated immediately and then permanently erased - along with the channels, schemas,
        and signals you own, and your messages in other people's channels - after a 7-day grace period, by an
        automated job, except where we're required to retain something longer by law.
      </P>

      <H2>6. Your choices and rights</H2>
      <Ul>
        <li>You can update your profile information (nickname, avatar) directly in the app at any time.</li>
        <li>
          <strong>Export your data</strong> - Settings &rsaquo; Profile &rsaquo; "Download my data" gives you a
          JSON file of your profile, channel memberships, and every signal you've sent (GDPR Art. 15 and 20).
        </li>
        <li>
          <strong>Delete your account</strong> - Settings &rsaquo; Profile &rsaquo; "Delete account", no email to
          support required (GDPR Art. 17). See Section 7.
        </li>
        <li>
          You can control non-essential cookies/local storage through the banner on this site or the "Cookie
          settings" row in Settings - see our <A to="/cookies">Cookie Policy</A>.
        </li>
        <li>
          If you're in the EU/UK or California, you have additional rights (access, correction, deletion,
          portability, restriction, or objection to processing) under GDPR or CCPA - the export and delete tools
          cover most of them directly, and you can contact us for anything else. You also have the right to lodge a
          complaint with your local supervisory authority.
        </li>
      </Ul>

      <H2>7. Deleting your account</H2>
      <P>
        Open Settings &rsaquo; Profile &rsaquo; "Delete account" in the web or iOS app. Your account is deactivated
        right away; after a 7-day grace period an automated job permanently erases your profile, the
        channels/schemas/signals you own and all messages in them, your memberships and authored messages in
        other people's channels, and your entries in our search indexes. Signing back in during the grace period
        cancels the deletion. If you can't reach the app for any reason, email {CONTACT_EMAIL} from the address on
        the account.
      </P>

      <H2>8. Where your data is processed</H2>
      <P>
        zmist's application servers and database are hosted in the European Union. Some sub-processors (for example
        a content-delivery network, or an email provider) may process limited technical data or message metadata
        outside the EU; where that happens it is covered by an appropriate transfer mechanism such as the EU
        Standard Contractual Clauses. The current locations are listed on the <A to="/subprocessors">Sub-processors</A>{' '}
        page.
      </P>

      <H2>9. Children</H2>
      <P>zmist isn't directed at children under {MIN_AGE}, and we don't knowingly collect their information.</P>

      <H2>10. Changes to this policy</H2>
      <P>We'll update the date at the top of this page whenever this policy changes materially.</P>

      <H2>11. Contact</H2>
      <P>
        Questions about this policy or your data: {CONTACT_EMAIL}
        {REGISTERED_ADDRESS ? <> · {REGISTERED_ADDRESS}</> : null}
      </P>
    </LegalLayout>
  );
}
